import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryClient, queryKeys } from './query-client';
import { useWorkspaces } from './workspaces';
import { fetchTasks, unsettledTasksRefetchInterval, type MaestroAction, type OutreachQuestionsRead, type WebTask } from './tasks';
import { useAgentTaskInvalidation } from './agent-task-invalidation';
import type { OpenAsksRead } from './open-asks';
import { buildIslandQuickView, toQuickViewTask, type IslandQuickView, type QuickViewTask } from './island-quickview';

/**
 * The Maestro's in-flight non-task work, as the account-wide `/api/tasks` last reported it. The entry is
 * never fetched on its own — `fetchTasks` writes it as a sibling of the rows — so this is a cache
 * OBSERVER (`enabled: false`): it re-renders when a fetch lands and holds `[]` until one has. Absent ≡
 * empty, so an old server (or a not-yet-loaded list) claims no actions — never blue from ignorance.
 */
export function useMaestroActions(): MaestroAction[] {
  const { data } = useQuery<MaestroAction[]>({
    queryKey: queryKeys.maestroActions,
    enabled: false,
  });
  return data ?? [];
}

/**
 * The outreach-only open questions as the account-wide `/api/tasks` last reported them — a cache OBSERVER
 * on the same pattern as {@link useMaestroActions}. `null` until a read lands, or when the server omitted the
 * field: the island then shows task cards only and adds nothing.
 */
export function useOutreachQuestions(): OutreachQuestionsRead | null {
  const { data } = useQuery<OutreachQuestionsRead | null>({
    queryKey: queryKeys.outreachQuestions,
    enabled: false,
  });
  return data ?? null;
}

/**
 * The open asks (`lib/open-asks.ts`) as the account-wide `/api/tasks` last reported them — a cache OBSERVER like
 * {@link useOutreachQuestions}. `null` until a read lands, or when the server omitted the field.
 */
export function useOpenAsks(): OpenAsksRead | null {
  const { data } = useQuery<OpenAsksRead | null>({
    queryKey: queryKeys.openAsks,
    enabled: false,
  });
  return data ?? null;
}

export type IslandQuickViewState = { status: 'loading' } | { status: 'ready'; data: IslandQuickView } | { status: 'error' };

/**
 * The island quick-view's data — the peek the popover (web) / sheet (native) render.
 *
 * It reads the **account-wide `/api/tasks` list**, on the SAME React Query key the Tasks screen renders
 * from (`queryKeys.tasks(undefined)`), so the bubble is a projection of that screen rather than a
 * parallel list. It used to fan out over `/api/workspace-tasks?workspaceId=` once per workspace, and that
 * quietly made it a DIFFERENT list: that route returns raw AgentTasks scoped to a workspace plus one
 * level of child projects, so the bubble could never show an inbound outreach row (they have no
 * workspace), nor a task whose `workspaceId` is empty or whose Space is missing from the roster. A row on
 * the screen that cannot appear in the bubble is the same class of bug as the two disagreeing about order.
 *
 * Reading one account-wide entry also costs FEWER requests than the fan-out it replaces — one call
 * instead of one per Space — but it is a heavier call, and the pill is mounted on every screen: this is
 * now the app's ambient task read. Hence `staleTime` + `refetchOnMount: false`; freshness comes from the
 * agent-task duplexer below, which is the signal that actually means something changed — under
 * `useTasks`'s own floor (`unsettledTasksRefetchInterval`), which this observer takes for the reason that
 * timer exists: it was added because a dropped duplexer frame left THE PILL claiming work that had
 * finished, and the pill is mounted everywhere while `/tasks` is one screen. A floor only the Tasks screen
 * carried would leave the most visible reader of this entry the stalest one. Unsettled-gated and
 * foreground-only there, so a finished list still costs nothing.
 *
 * ⚠️ One consequence worth naming: `/api/tasks` WRITES on the read path (`reconcileAbandoned` settles
 * tasks whose loop died), so that reconciliation now runs from every screen rather than only from
 * `/tasks`. It is idempotent and finds nothing on the second pass, but it belongs off a GET; moving it is
 * tracked separately rather than folded into this change.
 */
export function useIslandQuickView(): IslandQuickViewState {
  const workspacesState = useWorkspaces();
  const workspaces = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
  // The Maestro's non-task work, folded into the blue card's rows and both working counts. Rides the same
  // fetch as the rows, so the two cannot be a poll apart.
  const maestroActions = useMaestroActions();
  // The outreach-only questions — their own cards on the orange card, and in the pill's number (web's pill
  // IS the builder's `waitingCount`). Same fetch as the rows, so a dedupe against them is never a poll apart.
  const outreachQuestions = useOutreachQuestions();
  // The open asks — in the pill's number by the shared rule (`lib/action-needed-count.ts`), drawn on the Tasks screen.
  const openAsks = useOpenAsks();

  const query = useQuery({
    queryKey: queryKeys.tasks(undefined),
    queryFn: () => fetchTasks(),
    staleTime: 60_000,
    refetchOnMount: false,
    refetchInterval: (data) => unsettledTasksRefetchInterval(data as WebTask[] | undefined),
    refetchIntervalInBackground: false,
  });

  // Freshness comes from here, not from mount — but through ONE shared listener that INVALIDATES, not a
  // per-instance `refetch()`. This used to be its own listener, and on `/tasks` it fired alongside
  // `useTasks()`'s own for a single duplexer event; since `refetch()` defaults to `cancelRefetch: true`
  // the two cancelled each other, so the screen issued two `/api/tasks` calls per event with the first
  // abandoned mid-flight. Multiply that by the pill mounting on every route and by `useIslandState` /
  // `useIslandAgentStarted` each instantiating this hook, and one frame became the measured 15-calls-in-89ms
  // storm. Invalidation is idempotent per key, so the collapse is structural rather than a tuning knob.
  useAgentTaskInvalidation();

  // Data first: React Query keeps the last good list alongside an `error` status on a failed REFETCH, and
  // showing the rows we have beats blanking the bubble over a transient failure. Error only before the
  // first success; loading until then.
  if (!query.data) return query.status === 'error' ? { status: 'error' } : { status: 'loading' };

  const names = new Map(workspaces.map((workspace) => [workspace.id, workspace.name]));
  const tasks: QuickViewTask[] = query.data.map((task: WebTask) =>
    toQuickViewTask(task, (task.workspaceId && names.get(task.workspaceId)) || ''),
  );

  return {
    status: 'ready',
    data: buildIslandQuickView(tasks, Date.now(), maestroActions, outreachQuestions?.questions, outreachQuestions?.total, openAsks),
  };
}

/** The open quick-view's freshness floor — the same ~20s cadence the task-liveness poll established. */
export const ISLAND_OPEN_POLL_MS = 20_000;

/**
 * A ~20s re-read of the island's two sources — the account-wide tasks list AND the per-workspace
 * aggregate — WHILE the quick-view is open. The card is a live surface when it is up: the duplexer push
 * and the unsettled-list floor still do the ambient work, but a reader looking at "agents at work" rows
 * deserves a bounded staleness regardless of what the push delivers. The timer stops on close and on
 * unmount — every exit path clears it, because the effect owns it.
 */
export function useIslandOpenRefresh(open: boolean): void {
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setInterval(() => {
      void queryClient.refetchQueries({ queryKey: queryKeys.tasks(undefined), exact: true });
      // Prefix match — one refetch covers every workspace's entry.
      void queryClient.refetchQueries({ queryKey: ['workspace-tasks'] });
    }, ISLAND_OPEN_POLL_MS);
    return () => window.clearInterval(id);
  }, [open]);
}
